import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { formatSignedBillPercent } from "../../billDiscount";

/** Клиентският процент по сметката, както го подава модалът за закриване. */
export interface BillClientDiscount {
  /** bill.total_discount: положителна при отстъпка, отрицателна при надбавка. */
  amount: number;
  /** Знаков процент (>0 отстъпка, <0 надбавка). */
  percent: number;
  /** Отстъпката в € на цялата сметка (действащата) — вече извадена от
   *  „Общо", но не от `amount`; 0/липсва — без ред. */
  billDiscount?: number;
}

interface BillTotalsBreakdownProps {
  /** Общо за плащане (вече след клиентския процент). */
  total: number;
  /** При закачен клиент — редовете „Сума без отстъпка" и „Отстъпка на
   *  клиента (−X%)" над „Общо"; при 0% редове няма. */
  discount?: BillClientDiscount | null;
  /** Цветът на разделителя над блока (панелите ползват различен). */
  dividerClassName?: string;
  /** Режимът с бакшиш (дизайнът „Закриване — всички комбинации"): голямото
   *  „Общо" е дължимото с метода на сметката (поръчаното + бакшиш със същия
   *  метод), а под него — един ред откъде идва. Редовете за клиента остават
   *  сметнати от `total` (поръчаното). Без тях блокът е старият. */
  displayTotal?: number;
  note?: ReactNode;
}

/**
 * Блокът „Общо" в дъното на лявата колона на модала за закриване — общ за
 * „В брой" и „Смесено". С клиент показва и откъде идва сумата: основата
 * (total + total_discount + отстъпката в € на сметката), корекцията на
 * клиента със знак и отстъпката в € (ако има).
 */
export const BillTotalsBreakdown = ({
  total,
  discount = null,
  dividerClassName = "bg-pos-line",
  displayTotal,
  note,
}: BillTotalsBreakdownProps) => {
  const { t } = useTranslation();
  const showDiscount = !!discount && discount.percent !== 0;
  const isSurcharge = !!discount && discount.percent < 0;
  const billDiscount = discount?.billDiscount ?? 0;
  const baseTotal = total + (discount?.amount ?? 0) + billDiscount;

  return (
    <div className="mt-auto pt-6">
      <div className={`mb-3 h-px ${dividerClassName}`} />
      {showDiscount && (
        <div className="mb-2 flex flex-col gap-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[13px] font-medium text-pos-soft2">
              {t(
                isSurcharge
                  ? "closeTable.amountBeforeSurcharge"
                  : "closeTable.amountBeforeDiscount",
              )}
            </span>
            {/* Зачертано е само числото — text-decoration се наследява от
                вътрешните span-ове и не може да се махне от дете, затова
                „EUR" стои извън зачертания span */}
            <span className="font-pos-mono text-[15px] font-semibold text-pos-muted [font-variant-numeric:tabular-nums]">
              <span className="line-through">{baseTotal.toFixed(2)}</span>{" "}
              <span className="text-[11px]">EUR</span>
            </span>
          </div>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[13px] font-medium text-pos-soft2">
              {t(
                isSurcharge
                  ? "closeTable.clientSurcharge"
                  : "closeTable.clientDiscount",
                { percent: formatSignedBillPercent(discount.percent) },
              )}
            </span>
            <span className="font-pos-mono text-[15px] font-semibold text-pos-accent-ink [font-variant-numeric:tabular-nums]">
              {/* Отстъпката НАМАЛЯВА (−), надбавката ДОБАВЯ (+) */}
              {isSurcharge ? "+" : "-"}
              {Math.abs(discount.amount).toFixed(2)}{" "}
              <span className="text-[11px] text-pos-muted">EUR</span>
            </span>
          </div>
          {billDiscount >= 0.005 && (
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[13px] font-medium text-pos-soft2">
                {t("itemDiscounts.billTitle")}
              </span>
              <span className="font-pos-mono text-[15px] font-semibold text-pos-accent-ink [font-variant-numeric:tabular-nums]">
                -{billDiscount.toFixed(2)}{" "}
                <span className="text-[11px] text-pos-muted">EUR</span>
              </span>
            </div>
          )}
        </div>
      )}
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[13px] font-bold text-pos-ink">
          {t("common.total")}
        </span>
        <span className="font-pos-mono text-[28px] font-bold tracking-[-1px] text-pos-accent">
          {(displayTotal ?? total).toFixed(2)} <span className="text-lg">EUR</span>
        </span>
      </div>
      {note && (
        <div className="text-right text-[12px] font-semibold text-pos-soft2">{note}</div>
      )}
    </div>
  );
};
